import { useBrowserPush } from '@/features/admin/PushSetting';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useSession } from '@/stores/session';
import { useNotificationPreferences, type NotificationCategory, type NotificationPreferences } from '@/stores/notificationPreferences';
import { Icon } from '@/components/Icon';
import { installGuideSnoozed } from './InstallGuidePage';
import { playNotificationTone, speakNotification } from '@/shell/notificationAudio';
import s from './NotificationSettingsPage.module.css';

const CATEGORIES: { key: NotificationCategory; title: string; detail: string; icon: string; tone: 'blue' | 'violet' | 'green' }[] = [
  { key: 'review', title: '订单待审核', detail: '管理员、业务主管', icon: 'check', tone: 'blue' },
  { key: 'follow', title: '分配给我跟单', detail: '跟单员', icon: 'user', tone: 'violet' },
  { key: 'stock', title: '库存已满足 / 部分满足', detail: '横幅不会自动消失，直到你处理', icon: 'box', tone: 'green' },
  { key: 'change', title: '订单变更', detail: '待审核、已通过、已驳回、待你知晓', icon: 'edit', tone: 'violet' },
  { key: 'shipped', title: '已发货', detail: '业务员关心；仓库自己发的不提醒', icon: 'truck', tone: 'blue' },
];

const toneClass: Record<string, string> = { blue: 'blueIcon', violet: 'violetIcon', green: 'greenIcon' };

async function previewSound(name: string, speech: boolean) {
  await playNotificationTone();
  if (speech) window.setTimeout(() => speakNotification(`${name}，有订单待审核`), 650);
}

export function NotificationSettingsPage() {
  const navigate = useNavigate();
  const user = useSession((state) => state.user);
  const value = useNotificationPreferences((state) => state.value);
  const loaded = useNotificationPreferences((state) => state.loaded);
  const push = useBrowserPush();
  const [status, setStatus] = useState('');
  const ios = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const standalone = window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;

  useEffect(() => { if (user?.id) void useNotificationPreferences.getState().load(user.id); }, [user?.id]);
  useEffect(() => {
    if (!user?.id || !ios || standalone || !window.isSecureContext || installGuideSnoozed()) return;
    const key = `ims.v2.install-h8-shown.${user.id}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
    } catch { return; }
    navigate('/me/install', { state: { returnTo: '/me/notifications' }, replace: true });
  }, [user?.id, ios, standalone, navigate]);

  const save = async (next: NotificationPreferences) => {
    if (!user) return;
    setStatus('保存中…');
    const result = await useNotificationPreferences.getState().save(user.id, next);
    setStatus(result === 'synced' ? '已同步当前账号的偏好' : '已保存在这台设备；服务端只读或暂不可用');
  };
  const toggleSound = async () => {
    if (!loaded) return;
    if (!value.sound) {
      try { await previewSound(user?.name || '你', false); }
      catch { setStatus('浏览器阻止了声音播放，请允许此网站播放声音后重试。'); return; }
    }
    await save({ ...value, sound: !value.sound });
  };
  const preview = async () => {
    try { await previewSound(user?.name || '你', value.speech); setStatus('已试听一条提醒'); }
    catch { setStatus('浏览器阻止了声音播放，请检查静音和媒体权限。'); }
  };
  const systemStatus = !window.isSecureContext ? '需 HTTPS' : ios && !standalone ? '需先添加到主屏幕'
    : push.error || push.status;

  return <div className={s.page}>
    <header className={s.header}><button type="button" onClick={() => navigate('/me')}>‹ 我的</button><h1>通知与声音</h1><span /></header>
    <main className={s.body}>
      {ios && !standalone && window.isSecureContext && <section className={s.install}>
        <b>iPhone 要先添加到主屏幕，才能在后台收到推送</b>
        <p>现在只有打开网页时才会响铃。添加后从主屏幕打开，回到这里查看「系统通知」。</p>
        <Link to="/me/install" state={{ returnTo: '/me/notifications' }}>查看添加方法 ›</Link>
      </section>}
      <h2>提醒方式</h2>
      <div className={s.group}>
        <div className={s.row}><span className={`${s.rowIcon} ${s.blueIcon}`}><Icon name="inbox" size={20} /></span><div><b>站内提醒</b><small>打开时从顶部弹出，也进通知中心</small></div><span className={s.permanent}>始终开启</span></div>
        {ios && !standalone ? <Link to="/me/install" state={{ returnTo: '/me/notifications' }} className={`${s.row} ${s.systemLink}`}><span className={`${s.rowIcon} ${s.blueIcon}`}><Icon name="bell" size={20} /></span><div><b>系统通知</b><small>锁屏和后台也能收到</small></div><span className={s.muted}>{systemStatus}</span><Icon name="chev" size={14} /></Link>
          : <div className={s.row}><span className={`${s.rowIcon} ${s.blueIcon}`}><Icon name="bell" size={20} /></span><div><b>系统通知</b><small>锁屏和后台也能收到</small></div><span className={s.muted}>{systemStatus}</span><button type="button" className={`${s.switch} ${push.active ? s.on : ''}`} role="switch" aria-checked={push.active} aria-label="系统通知" disabled={push.disabled || !window.isSecureContext} onClick={() => void push.toggle()} /></div>}
        <div className={s.row}><span className={`${s.rowIcon} ${s.blueIcon}`}><Icon name="sound" size={20} /></span><div><b>提示音</b><small>不同业务不同音调</small></div><button type="button" className={`${s.switch} ${value.sound ? s.on : ''}`} role="switch" aria-checked={value.sound} aria-label="提示音" disabled={!loaded} onClick={() => void toggleSound()} /></div>
        <div className={s.row}><span className={`${s.rowIcon} ${s.blueIcon}`}><Icon name="msg" size={20} /></span><div><b>语音播报</b><small>提示音后念一句「{user?.name?.slice(-2) || '你'}，有订单待审核」</small></div><button type="button" className={`${s.switch} ${value.speech ? s.on : ''}`} role="switch" aria-checked={value.speech} aria-label="语音播报" disabled={!loaded || !value.sound} onClick={() => void save({ ...value, speech: !value.speech })} /></div>
      </div>
      <button type="button" className={s.preview} onClick={() => void preview()}><Icon name="sound" size={18} />试听一条提醒</button>
      <h2>按类型响铃 <small>关掉的只进通知中心</small></h2>
      <div className={s.group}>{CATEGORIES.map(({ key, title, detail, icon, tone }) => <div className={s.row} key={key}>
        <span className={`${s.rowIcon} ${s[toneClass[tone]]}`}><Icon name={icon} size={20} /></span><div><b>{title}</b><small>{detail}</small></div>
        <button type="button" className={`${s.switch} ${value.categories[key] ? s.on : ''}`} role="switch" aria-checked={value.categories[key]} aria-label={`${title}响铃`} disabled={!loaded} onClick={() => void save({ ...value, categories: { ...value.categories, [key]: !value.categories[key] } })} />
      </div>)}</div>
      <p className={s.help}>手机静音时不响铃，语音播报跟随媒体音量。站内提醒在打开网页时显示；系统通知开启后可在后台接收，实际送达由浏览器、设备权限及服务器推送配置决定。</p>
      {status && <p className={s.status} role="status">{status}</p>}
    </main>
  </div>;
}
